import { html } from '@design.estate/dees-element';
import type { DeesHarnessChat } from './dees-harness-chat.js';
import type { IHarnessMessage, IHarnessPermissionRequest } from '../interfaces.js';
import { harnessDemoConversation, makeStreamingSimulator } from '../harness.demodata.js';

const toolShowcase = (): IHarnessMessage[] => {
  const now = Date.now();
  return [
    {
      id: 'demo-t1',
      role: 'tool',
      text: '',
      createdAt: now - 240_000,
      toolCall: {
        id: 'call-1',
        name: 'run_command',
        status: 'completed',
        input: { command: 'pnpm test --filter sitemap', cwd: '/srv/app' },
        output: { exitCode: 0, stdout: '14 tests passed' },
        startedAt: now - 250_000,
        finishedAt: now - 243_000,
      },
    },
    {
      id: 'demo-t2',
      role: 'tool',
      text: '',
      createdAt: now - 200_000,
      toolCall: {
        id: 'call-2',
        name: 'write_file',
        status: 'completed',
        input: {
          path: '/srv/app/src/jobs/sitemap.job.ts',
          oldContent: "const cacheKey = hash(locales);",
          content: "const cacheKey = hash(routeManifest);",
        },
        startedAt: now - 205_000,
        finishedAt: now - 202_000,
      },
    },
    {
      id: 'demo-t3',
      role: 'tool',
      text: '',
      createdAt: now - 150_000,
      toolCall: {
        id: 'call-3',
        name: 'kb__lookup_article',
        status: 'completed',
        mcp: {
          serverName: 'kb',
          toolName: 'lookup_article',
          annotations: { title: 'Knowledge Lookup', readOnlyHint: true, openWorldHint: true },
        },
        input: { query: 'sitemap cache determinism' },
        contentBlocks: [
          { type: 'text', text: 'Found “Deterministic sitemap builds”.' },
          {
            type: 'resource_link',
            uri: 'https://kb.example.com/articles/812',
            name: 'deterministic-sitemap-builds',
            title: 'Deterministic sitemap builds',
            mimeType: 'text/html',
          },
        ],
        startedAt: now - 155_000,
        finishedAt: now - 152_000,
      },
    },
  ];
};

const pendingPermission: IHarnessPermissionRequest = {
  id: 'perm-demo',
  title: 'Write file',
  type: 'write',
  createdAt: Date.now(),
  metadata: { path: '/srv/app/src/jobs/sitemap.job.ts', bytes: 812 },
};

let stopStream: (() => void) | undefined;

const chatOf = (event: MouseEvent): DeesHarnessChat | null =>
  (event.target as HTMLElement).closest('.chatDemo')?.querySelector('dees-harness-chat') ?? null;

const simulate = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  stopStream?.();
  const message: IHarnessMessage = {
    id: `stream-${Date.now()}`,
    role: 'assistant',
    text: '',
    createdAt: Date.now(),
    streaming: true,
  };
  chat.messages = [...chat.messages, message];
  chat.status = { type: 'busy', message: 'Responding…' };
  stopStream = makeStreamingSimulator(
    {
      applyDelta: (delta) => {
        chat.applyDelta(delta);
        if (delta.type === 'message-end') {
          chat.status = { type: 'idle' };
        }
      },
    },
    message.id,
  );
};

const askPermission = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  chat.permissions = [...chat.permissions, { ...pendingPermission, id: `perm-${Date.now()}` }];
};

const toggleError = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  chat.status = chat.status.type === 'error'
    ? { type: 'idle' }
    : { type: 'error', message: 'The model endpoint returned 429 — retrying in 20s.' };
};

const resetChat = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  stopStream?.();
  chat.messages = [...harnessDemoConversation(), ...toolShowcase()];
  chat.permissions = [];
  chat.status = { type: 'idle' };
};

export const demoFunc = () => html`
  <style>
    .chatDemo {
      display: grid;
      grid-template-rows: auto minmax(0, 1fr);
      gap: 12px;
      height: 640px;
      max-width: 860px;
      margin: 40px auto;
    }
    .chatDemo .controls {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
    .chatDemo dees-harness-chat {
      border: 1px solid var(--dees-color-border-subtle);
      border-radius: var(--dees-radius-xl);
      overflow: hidden;
    }
  </style>
  <div
    class="chatDemo"
    @harness-send=${(event: CustomEvent) => console.log('harness-send', event.detail)}
    @harness-permission-response=${(event: CustomEvent) => {
      console.log('harness-permission-response', event.detail);
      const chat = (event.target as HTMLElement).closest('.chatDemo')?.querySelector('dees-harness-chat');
      if (chat) {
        setTimeout(() => {
          chat.permissions = chat.permissions.filter((request) => request.id !== event.detail.requestId);
        }, 600);
      }
    }}
    @harness-new-session=${() => console.log('harness-new-session')}
    @harness-open-sessions=${() => console.log('harness-open-sessions')}
    @harness-abort=${() => {
      stopStream?.();
      console.log('harness-abort');
    }}
  >
    <div class="controls">
      <dees-button type="accent" @clicked=${simulate}>Simulate streaming</dees-button>
      <dees-button @clicked=${askPermission}>Ask permission</dees-button>
      <dees-button @clicked=${toggleError}>Toggle error status</dees-button>
      <dees-button type="ghost" @clicked=${resetChat}>Reset</dees-button>
    </div>
    <dees-harness-chat
      heading="Deploy assistant"
      subheading="acme-app · main · connected"
      model="gpt-5.5"
      reasoningEffort="high"
      .modelOptions=${['gpt-5.5', 'gpt-5', 'o4-mini']}
      .effortOptions=${['xhigh', 'high', 'medium', 'low']}
      .usage=${{ inputTokens: 18_400, outputTokens: 3_100, totalTokens: 21_500 }}
      .messages=${[...harnessDemoConversation(), ...toolShowcase()]}
    ></dees-harness-chat>
  </div>
`;
